// src/components/ModernGanttView.tsx
import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../../lib/supabase";
import { 
  FiUser, FiFolder, FiChevronRight, FiChevronDown
} from "react-icons/fi";
import { useTasksQuery } from "../../hooks/useTasksQuery";
import GlobalSidebar from "../layout/GlobalSidebar";
import GlobalHeader from "../layout/GlobalHeader";
import TaskDetailsModal from "../modals/TaskDetailsModal";
import TaskEditModal from "../modals/TaskEditModal";
import type { Task, Project, Company, Priority } from "../../types/domain";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday, isSameDay } from "date-fns";
import { es } from "date-fns/locale";
import "../ModernGanttView.css";

interface GanttProject {
  id: string;
  name: string;
  tasks: Task[];
  expanded: boolean;
  company?: Company;
}

interface GanttViewProps {
  projectId?: string;
}

export default function ModernGanttView({ projectId }: GanttViewProps) {
  // Estados para filtros
  const [selectedProjectId, setSelectedProjectId] = useState<string>(projectId || "");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [priorityFilter, setPriorityFilter] = useState<Priority | "">("");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Hook para cargar tareas
  const { tasks, loading, error, reload, setFilters } = useTasksQuery({
    projectId: selectedProjectId || undefined,
    search: searchTerm.trim() || undefined,
    orderBy: "position",
    ascending: true,
    limit: 2000,
  });
  
  // Estados del componente
  const [projects, setProjects] = useState<Project[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  // Cargar proyectos y empresas
  useEffect(() => {
    const loadProjectsAndCompanies = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          const { data: allProjects } = await supabase
            .from("projects")
            .select("*")
            .order('name');

          if (allProjects) {
            setProjects(allProjects as Project[]);
          }

          const { data: allCompanies } = await supabase
            .from("companies")
            .select("*")
            .order('name');

          if (allCompanies) {
            setCompanies(allCompanies as Company[]);
          }
        }
      } catch (error) {
        console.error("Error cargando proyectos y empresas:", error);
      }
    };

    loadProjectsAndCompanies();
  }, []);

  // Keep query in sync with sidebar filters
  useEffect(() => {
    setFilters({
      projectId: selectedProjectId || undefined,
      search: (searchTerm || "").trim() || undefined,
    });
  }, [selectedProjectId, searchTerm, setFilters]);

  // Filtrar tareas aplicando filtros adicionales del sidebar
  const filteredTasks = useMemo(() => {
    if (!tasks) return [];

    let filtered = tasks.slice();
    
    // Filtrar por empresa via projects
    if (selectedCompanyId) {
      const allowedProjects = projects.filter(p => p.company_id === selectedCompanyId).map(p => p.id);
      filtered = filtered.filter(task => allowedProjects.includes(task.project_id));
    }
    
    // Filtros de sidebar
    if (priorityFilter && priorityFilter !== "") {
      filtered = filtered.filter(task => task.priority === priorityFilter);
    }
    
    if (assigneeFilter && assigneeFilter !== "") {
      filtered = filtered.filter(task => task.assignee_id === assigneeFilter);
    }
    
    if (searchTerm && searchTerm.trim() !== "") {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(task => 
        task.title.toLowerCase().includes(searchLower) ||
        (task.description && task.description.toLowerCase().includes(searchLower))
      );
    }
    
    return filtered;
  }, [tasks, selectedCompanyId, projects, priorityFilter, assigneeFilter, searchTerm]);

  // Agrupar tareas por proyecto
  const ganttProjects: GanttProject[] = useMemo(() => {
    const projectMap = new Map<string, GanttProject>();

    // Agrupar tareas por proyecto
    filteredTasks.forEach(task => {
      if (!task.project_id) return;

      if (!projectMap.has(task.project_id)) {
        const project = projects.find(p => p.id === task.project_id);
        const company = companies.find(c => c.id === project?.company_id);
        
        projectMap.set(task.project_id, {
          id: task.project_id,
          name: project?.name || 'Proyecto Sin Nombre',
          tasks: [],
          expanded: expandedProjects.has(task.project_id),
          company
        });
      }

      projectMap.get(task.project_id)!.tasks.push(task);
    });

    return Array.from(projectMap.values());
  }, [filteredTasks, projects, companies, expandedProjects]);

  // Generar días del mes actual
  const monthDays = useMemo(() => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  // Limpiar filtros
  const clearFilters = () => {
    setPriorityFilter("");
    setAssigneeFilter("");
    setSearchTerm("");
  };

  // Toggle expand/collapse proyecto
  const toggleProject = (projectId: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  // Calcular ancho de barra basado en fecha de inicio y fin
  const getTaskBarStyle = (task: Task) => {
    // Priorizar fechas de inicio/fin sobre deadline
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    if (task.start_date) {
      startDate = new Date(task.start_date);
    }
    if (task.end_date) {
      endDate = new Date(task.end_date);
    }
    
    // Si no hay fechas de inicio/fin, usar deadline como punto único
    if (!startDate && !endDate && task.deadline) {
      startDate = new Date(task.deadline);
      endDate = new Date(task.deadline);
    }

    // Si no hay fechas válidas, no mostrar barra
    if (!startDate && !endDate) {
      return { width: '0%', left: '0%' };
    }

    // Si solo hay una fecha, usarla para ambos
    if (!startDate) startDate = endDate!;
    if (!endDate) endDate = startDate!;

    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);

    // Verificar si la tarea está en el rango del mes actual
    if (endDate < monthStart || startDate > monthEnd) {
      return { width: '0%', left: '0%' };
    }

    // Ajustar fechas al rango del mes actual
    const clampedStart = startDate < monthStart ? monthStart : startDate;
    const clampedEnd = endDate > monthEnd ? monthEnd : endDate;

    const startDayIndex = monthDays.findIndex(day => isSameDay(day, clampedStart));
    const endDayIndex = monthDays.findIndex(day => isSameDay(day, clampedEnd));

    if (startDayIndex === -1) return { width: '0%', left: '0%' };

    const leftPercentage = (startDayIndex / monthDays.length) * 100;
    const dayWidth = (1 / monthDays.length) * 100;
    const widthPercentage = endDayIndex === -1 ? 
      dayWidth : 
      Math.max(dayWidth, ((endDayIndex - startDayIndex + 1) / monthDays.length) * 100);

    return {
      left: `${leftPercentage}%`,
      width: `${widthPercentage}%`
    };
  };

  // Obtener clase de prioridad
  const getPriorityClass = (priority: Priority | null) => {
    switch (priority) {
      case 'urgent': return 'urgent';
      case 'high': return 'high';
      case 'medium': return 'medium';
      case 'low': return 'low';
      default: return 'normal';
    }
  };

  const handleProjectSelect = (newProjectId: string) => {
    setSelectedProjectId(newProjectId);
    if (newProjectId) {
      const project = projects.find(p => p.id === newProjectId);
      if (project && project.company_id) {
        setSelectedCompanyId(project.company_id);
      }
    }
  };

  return (
    <>
      {/* Global Header */}
      <GlobalHeader
        selectedProjectId={selectedProjectId}
        selectedCompanyId={selectedCompanyId}
        projects={projects}
        companies={companies}
        viewType="gantt"
        currentDate={currentDate}
        onDateChange={setCurrentDate}
        onRefreshClick={reload}
        onCreateTask={() => {
          setShowCreateTaskModal(true);
        }}
        onUserClick={() => {
          // Aquí puedes agregar funcionalidad del usuario
        }}
      />
      
      {/* Global Sidebar */}
      <GlobalSidebar
        selectedProjectId={selectedProjectId}
        selectedCompanyId={selectedCompanyId}
        priorityFilter={priorityFilter}
        assigneeFilter={assigneeFilter}
        searchTerm={searchTerm}
        onProjectChange={handleProjectSelect}
        onCompanyChange={setSelectedCompanyId}
        onPriorityChange={setPriorityFilter}
        onAssigneeChange={setAssigneeFilter}
        onSearchChange={setSearchTerm}
        onClearFilters={clearFilters}
        projects={projects}
        companies={companies}
        tasks={tasks}
        showFilters={true}
        showProjectStats={true}
      />

      <div className="gantt-layout">
        {/* Main Content */}
        <div className="gantt-main">

        {/* Gantt Content */}
        <div className="gantt-content">
          {loading && (
            <div className="loading-state">
              Cargando proyectos...
            </div>
          )}
          
          {error && (
            <div className="error-state">
              ⚠️ {error}
            </div>
          )}

          {!loading && !error && (
            <div className="gantt-container">
              {/* Header con días */}
              <div className="gantt-header">
                <div className="tasks-column-header">
                  <span>Proyectos y Tareas</span>
                </div>
                <div className="timeline-header">
                  {monthDays.map((day, index) => (
                    <div
                      key={index}
                      className={`timeline-day ${isToday(day) ? 'today' : ''}`}
                    >
                      <div className="day-number">{format(day, 'd')}</div>
                      <div className="day-name">{format(day, 'EEE', { locale: es })}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rows de proyectos y tareas */}
              <div className="gantt-rows">
                {ganttProjects.map(project => (
                  <div key={project.id} className="project-group">
                    {/* Row del proyecto */}
                    <div className="gantt-row project-row">
                      <div className="tasks-column">
                        <button
                          className="project-toggle"
                          onClick={() => toggleProject(project.id)}
                        >
                          {project.expanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                          <FiFolder size={16} />
                          <span className="project-name">{project.name}</span>
                          <span className="task-count">({project.tasks.length})</span>
                        </button>
                      </div>
                      <div className="timeline-column">
                        {/* Barra de resumen del proyecto */}
                        <div className="project-summary-bar"></div>
                      </div>
                    </div>

                    {/* Rows de tareas */}
                    {project.expanded && project.tasks.map(task => (
                      <div key={task.id} className="gantt-row task-row">
                        <div className="tasks-column">
                          <div className="task-item">
                            <div className="task-info">
                              <span 
                                className="task-title"
                                onClick={() => {
                                  setSelectedTask(task);
                                  setShowTaskDetails(true);
                                }}
                              >
                                {task.title}
                              </span>
                              <div className="task-meta">
                                {task.assignee_id && (
                                  <span className="assignee">
                                    <FiUser size={12} />
                                    {task.assignee_id}
                                  </span>
                                )}
                                {task.priority && (
                                  <span className={`priority ${getPriorityClass(task.priority)}`}>
                                    {task.priority}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="timeline-column">
                          {task.deadline && (
                            <div
                              className={`task-bar priority-${getPriorityClass(task.priority)} ${task.status === 'done' ? 'completed' : ''}`}
                              style={getTaskBarStyle(task)}
                              title={`${task.title} - ${task.deadline}`}
                            >
                              <div className="task-bar-content">
                                <span className="task-bar-text">{task.title}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}

                {ganttProjects.length === 0 && (
                  <div className="empty-state">
                    <p>No hay proyectos o tareas para mostrar</p>
                    <p>Selecciona un proyecto diferente o ajusta los filtros</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Modal de detalles de tarea */}
    {showTaskDetails && selectedTask && (
      <TaskDetailsModal
        task={selectedTask}
        labels={[]}
        allLabels={[]}
        onClose={() => {
          setShowTaskDetails(false);
          setSelectedTask(null);
        }}
        onChanged={reload}
        onDelete={async () => {
          setShowTaskDetails(false);
          setSelectedTask(null);
          await reload();
        }}
      />
    )}

    {/* Modal de crear tarea */}
    <TaskEditModal
      task={null}
      isOpen={showCreateTaskModal}
      onClose={() => setShowCreateTaskModal(false)}
      onSave={async (newTask: Task) => {
        await reload();
        setShowCreateTaskModal(false);
      }}
      projects={projects}
      showStatusField={true}
      showDateField={true}
    />
    </>
  );
}